import { rowsToWatchFields } from "./mcaWatchFieldsHelpers";
import { DEFAULT_ROUTING_ACTION_COLOR } from "../data/mcaEntities";

const toRefId = (v) => {
  if (!v) return null;
  if (typeof v === "string") return v;
  return v._id ?? null;
};

const normalizeNotificationPayload = (n) => {
  const notifyBy = n.notifyBy || "USERS";
  return {
    name: n.name?.trim(),
    channel: n.channel || "EMAIL",
    notifyOnStart: !!n.notifyOnStart,
    notifyOnComplete: n.notifyOnComplete !== false,
    notifyBy,
    notifyEmails:
      notifyBy === "EMAILS"
        ? (n.notifyEmails ?? []).map((e) => String(e).trim()).filter(Boolean)
        : [],
    notifyUsers:
      notifyBy === "USERS"
        ? (n.notifyUsers ?? []).map(toRefId).filter(Boolean)
        : [],
    notifyRoles:
      notifyBy === "USER_ROLE"
        ? (n.notifyRoles ?? []).map(toRefId).filter(Boolean)
        : [],
    notifyMaker: !!n.notifyMaker,
    notifyNextApprovers: !!n.notifyNextApprovers,
    notifyEntityUser: !!n.notifyEntityUser,
    notifyEntityClient: !!n.notifyEntityClient,
    notifyEntityConsultants: !!n.notifyEntityConsultants,
    subject:
      (n.channel || "EMAIL") === "EMAIL" ? n.subject?.trim() : undefined,
    message:
      (n.channel || "EMAIL") === "EMAIL" ? n.message?.trim() : undefined,
  };
};

export const buildMcaProcessPayload = (formValues, watchRows) => ({
  name: formValues.name?.trim(),
  cname: formValues.cname?.trim() || undefined,
  description: formValues.description?.trim() || undefined,
  status: formValues.status,
  triggerType: formValues.triggerType,
  priority: Number(formValues.priority ?? 0),
  triggerConfig: {
    targetEntity: formValues.triggerConfig.targetEntity,
    action: formValues.triggerConfig.action,
    watchFieldLogic: formValues.triggerConfig.watchFieldLogic ?? "OR",
    watchFields: rowsToWatchFields(watchRows),
    allowedMakers: (formValues.triggerConfig.allowedMakers ?? [])
      .map(toRefId)
      .filter(Boolean),
    allowedMakerRoles: (formValues.triggerConfig.allowedMakerRoles ?? [])
      .map(toRefId)
      .filter(Boolean),
  },
  steps: (formValues.steps ?? []).map((step, idx) => ({
    order: step.order ?? idx + 1,
    name: step.name?.trim(),
    description: step.description?.trim() || undefined,
    approvalType: step.approvalType || "ANY",
    approvers: (step.approvers ?? []).map(toRefId).filter(Boolean),
    approverRoles: (step.approverRoles ?? []).map(toRefId).filter(Boolean),
    checklist: (step.checklist ?? [])
      .filter((c) => c.title?.trim())
      .map((c) => ({
        title: c.title.trim(),
        hint: c.hint?.trim() || undefined,
        required: !!c.required,
      })),
    setValues: (step.setValues ?? [])
      .filter((v) => v.field?.trim())
      .map((v) => ({
        field: v.field.trim(),
        value: v.value,
      })),
    notifications: (step.notifications ?? [])
      .filter((n) => n.name?.trim())
      .map(normalizeNotificationPayload),
    stepFields: (step.stepFields ?? [])
      .filter((f) => f.label?.trim())
      .map((f) => ({
        label: f.label.trim(),
        cname: f.cname?.trim() || undefined,
        inputType: f.inputType || "TEXT",
        required: !!f.required,
      })),
    routingActions: (step.routingActions ?? [])
      .filter((a) => a.label?.trim())
      .map((a) => ({
        type: a.type || "GO_TO_STEP",
        label: a.label.trim(),
        cname: a.cname?.trim() || undefined,
        color:
          a.color ||
          DEFAULT_ROUTING_ACTION_COLOR[a.type] ||
          DEFAULT_ROUTING_ACTION_COLOR.GO_TO_STEP,
        ...(a.type === "GO_TO_STEP" && a.targetStepOrder != null
          ? { targetStepOrder: Number(a.targetStepOrder) }
          : {}),
      })),
    includeWhen: step.includeWhen?.field
      ? {
          field: step.includeWhen.field.trim(),
          condition: step.includeWhen.condition,
        }
      : undefined,
    timeoutHours: step.timeoutHours || undefined,
  })),
  onTerminate: {
    setValues: (formValues.onTerminate?.setValues ?? [])
      .filter((v) => v.field?.trim())
      .map((v) => ({
        field: v.field.trim(),
        value: v.value,
      })),
  },
  finalAction: {
    setStatus: undefined,
    setFields: {},
  },
});
